import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { IActionButton } from '@appTypes';
import { useDownloadSignedCustomerAgreement } from '@hooks/useDownloadSignedCustomerAgreement';
import { useDownloadCustomerAgreementModal } from '@hooks/useModal/hooks/useDownloadCustomerAgreementModal';
import { useFileUploadModal } from '@hooks/useModal/hooks/useFileUploadModal';
import {
  CustomerAgreementSigningOverview,
  ManualIdentityUploadSummary,
} from '../types';

interface IApplicationDocumentUploads {
  customerAgreementSigningOverview: CustomerAgreementSigningOverview;
  externalContractUuid: string | undefined;
  manualIdentityUploadSummary: ManualIdentityUploadSummary;
  queryKey: QueryKey;
}

export const useGetApplicationDocumentButtons = ({
  customerAgreementSigningOverview,
  externalContractUuid,
  manualIdentityUploadSummary,
  queryKey,
}: IApplicationDocumentUploads) => {
  const queryClient = useQueryClient();

  const {
    hasDownloadedAgreement,
    hasUploadedSignedAgreement,
    isManualSigningRequired,
  } = customerAgreementSigningOverview;
  const { isManualIdentityDocumentUploadRequired } =
    manualIdentityUploadSummary;

  const canUploadManualAgreement = [
    isManualSigningRequired,
    hasDownloadedAgreement,
    !hasUploadedSignedAgreement,
  ].every(Boolean);

  const shouldShowFileUploadModal =
    canUploadManualAgreement || isManualIdentityDocumentUploadRequired;

  const fileUploadModal = useFileUploadModal({
    acceptedFiles: ['.pdf', '.jpeg', '.jpg'],
    dependencies: [shouldShowFileUploadModal],
    externalContractUuid,
    dependencyValues: {
      isManualSigningRequired,
      hasDownloadedAgreement,
      hasUploadedSignedAgreement,
      isManualIdentityDocumentUploadRequired,
    },
    onClose: () => queryClient.invalidateQueries(queryKey),
  });

  const fileDownloadModal = useDownloadCustomerAgreementModal({
    dependencies: [isManualSigningRequired, !hasUploadedSignedAgreement],
    externalApplicationUuid: externalContractUuid,
    queryKey,
  });

  const downloadSignedAgreementButton = useDownloadSignedCustomerAgreement(
    externalContractUuid,
    hasUploadedSignedAgreement,
  );

  return [
    downloadSignedAgreementButton,
    fileDownloadModal?.button,
    fileUploadModal?.button,
  ].filter(Boolean) as IActionButton[];
};
